import { useCallback, useEffect, useRef, useState } from 'react';

export interface Artboard {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  name: string;
  color: string;
}

export interface CanvasState {
  scale: number;
  offset: { x: number; y: number };
  zoomPercentage: number;
}

export function useCanvasTransform(
  canvasRef: React.RefObject<HTMLCanvasElement>,
  artboards: Artboard[],
  initialScale: number = 0.3,
  getIsPanning?: () => boolean
) {
  const customDrawRef = useRef<null | ((ctx: CanvasRenderingContext2D, state: CanvasState) => void)>(null);
  const [canvasState, setCanvasState] = useState<CanvasState>({
    scale: initialScale,
    offset: { x: 700, y: 200 },
    zoomPercentage: Math.round(initialScale * 100)
  });
  const rafIdRef = useRef<number | null>(null);
  const renderQueuedRef = useRef(false);
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const getIsPanningRef = useRef<(() => boolean) | undefined>(getIsPanning);
  
  // getIsPanning setter 함수
  const setGetIsPanning = useCallback((fn: (() => boolean) | undefined) => {
    getIsPanningRef.current = fn;
  }, []);

  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#464646';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.translate(canvasState.offset.x, canvasState.offset.y);
    ctx.scale(canvasState.scale, canvasState.scale);

    // viewport(world) 영역 계산
    const viewLeft = (-canvasState.offset.x) / canvasState.scale;
    const viewTop = (-canvasState.offset.y) / canvasState.scale;
    const viewRight = (canvas.width - canvasState.offset.x) / canvasState.scale;
    const viewBottom = (canvas.height - canvasState.offset.y) / canvasState.scale;

    const abIntersects = (ab: Artboard) => {
      const abRight = ab.x + ab.width;
      const abBottom = ab.y + ab.height;
      return !(ab.x > viewRight || abRight < viewLeft || ab.y > viewBottom || abBottom < viewTop);
    };

    artboards.forEach(ab => {
      if (!abIntersects(ab)) return;
      ctx.fillStyle = ab.color;
      ctx.fillRect(ab.x, ab.y, ab.width, ab.height);
      ctx.strokeStyle = '#ddd';
      ctx.lineWidth = 1 / canvasState.scale;
      ctx.strokeRect(ab.x, ab.y, ab.width, ab.height);
    });

    // 사용자 정의 드로우(객체/임시 도형 등)
    if (customDrawRef.current) {
      customDrawRef.current(ctx, canvasState);
    }
  }, [canvasRef, canvasState, artboards]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const parent = canvas.parentElement || canvas;

    const applySize = () => {
      const w = Math.max(1, parent.clientWidth);
      const h = Math.max(1, parent.clientHeight);
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
        requestRender();
      }
    };

    applySize();
    const ro = new ResizeObserver(applySize);
    ro.observe(parent);
    resizeObserverRef.current = ro;
    return () => {
      ro.disconnect();
      resizeObserverRef.current = null;
    };
  }, [canvasRef]);

  const setCustomDraw = (fn: null | ((ctx: CanvasRenderingContext2D, state: CanvasState) => void)) => {
    customDrawRef.current = fn;
  };

  const requestRender = () => {
    if (renderQueuedRef.current) return;
    renderQueuedRef.current = true;
    rafIdRef.current && cancelAnimationFrame(rafIdRef.current);
    rafIdRef.current = requestAnimationFrame(() => {
      renderQueuedRef.current = false;
      renderCanvas();
    });
  };

  const wasPanningRef = useRef(false);
  const lastPanningStateRef = useRef(false);

  // renderCanvas 변경 시 렌더링 (패닝 중이면 스킵)
  useEffect(() => {
    // 패닝 중이면 렌더링 스킵
    if (getIsPanningRef.current && getIsPanningRef.current()) {
      wasPanningRef.current = true;
      return;
    }
    
    renderCanvas();
  }, [renderCanvas]);

  // 패닝 상태 모니터링 (패닝 종료 시 렌더링 트리거)
  useEffect(() => {
    const checkPanning = () => {
      const isPanning = getIsPanningRef.current ? getIsPanningRef.current() : false;
      
      // 패닝이 끝났을 때 (이전에 패닝 중이었고 지금은 패닝 중이 아님)
      if (wasPanningRef.current && !isPanning) {
        wasPanningRef.current = false;
        requestRender();
      } else if (isPanning) {
        wasPanningRef.current = true;
      }
      
      lastPanningStateRef.current = isPanning;
    };

    // 주기적으로 패닝 상태 체크 (약 60fps)
    const intervalId = setInterval(checkPanning, 16);
    return () => clearInterval(intervalId);
  }, [requestRender]);

  return { canvasState, setCanvasState, renderCanvas, setCustomDraw, requestRender, setGetIsPanning };
}


